import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, XCircle, FileText, Download, ArrowRight, ChevronDown, Shield } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { exportAssurancePackagePDF, exportSectorProfilePDF } from '@/utils/exportUtils';
import GradeTooltip from '@/components/GradeTooltip';

const packages = [
  { id: 'reg', title: 'Regulator Package (EU AI Act)', desc: 'Complete evidence package for EU AI Act compliance audit. Includes decision records, control mappings, gap remediation evidence, and model cards.', completeness: 68, status: 'warning', records: 47832, lastGen: 'Mar 31, 2026' },
  { id: 'ins', title: 'Insurance Underwriting Package', desc: 'AI risk evidence package for cyber insurance underwriters. Demonstrates governance maturity, incident response, and decision auditability.', completeness: 81, status: 'good', records: 35210, lastGen: 'Feb 15, 2026' },
  { id: 'board', title: 'Board Risk Committee Pack', desc: 'Executive summary with posture trends, regulatory readiness, and risk event timeline. Non-technical format for board-level review.', completeness: 94, status: 'good', records: 47832, lastGen: 'Apr 1, 2026' },
  { id: 'audit', title: 'Internal Audit Package (ISO 42001)', desc: 'Full audit trail for internal AI governance review. Includes control testing evidence and exception history.', completeness: 55, status: 'warning', records: 11847, lastGen: 'Jan 31, 2026' },
  { id: 'us-ins', title: 'US Insurance & SEC Package', desc: 'AI governance evidence package for cyber insurance underwriters and SEC AI disclosure requirements. Demonstrates continuous auditability and material decision oversight.', completeness: 81, status: 'good', records: 47832, lastGen: 'Mar 15, 2026' },
  { id: 'nist-fed', title: 'NIST AI RMF · Federal Package', desc: 'Operational NIST AI RMF implementation evidence across all four functions. Suitable for federal procurement, FedRAMP AI governance, and continuous assurance requirements.', completeness: 71, status: 'warning', records: 47832, lastGen: 'Feb 28, 2026' },
  { id: 'soc2-ai', title: 'SOC 2 AI Attestation Package', desc: 'AI-specific evidence inputs for SOC 2 Type II examinations. Supports Processing Integrity, Security, and Confidentiality trust service criteria for AI systems. For use with your existing SOC 2 auditor and program.', completeness: 61, status: 'warning', records: 47832, lastGen: 'Never', disclaimer: 'Apex provides AI evidence inputs. Your licensed CPA firm issues the SOC 2 opinion.' },
];

const crosswalkRows = [
  { category: 'Risk Management', eu: 'covered', iso: 'covered', nist: 'covered', soc2: 'partial' },
  { category: 'Data Governance', eu: 'covered', iso: 'partial', nist: 'covered', soc2: 'covered' },
  { category: 'Transparency', eu: 'gap', iso: 'partial', nist: 'covered', soc2: 'partial' },
  { category: 'Human Oversight', eu: 'covered', iso: 'covered', nist: 'covered', soc2: 'covered' },
  { category: 'Quality Management', eu: 'gap', iso: 'covered', nist: 'partial', soc2: 'partial' },
  { category: 'Incident Response', eu: 'covered', iso: 'covered', nist: 'gap', soc2: 'covered' },
];

const statusIcon = (s) => {
  if (s === 'covered') return <CheckCircle2 size={14} className="text-[#10B981]" />;
  if (s === 'partial') return <AlertTriangle size={14} className="text-[#F59E0B]" />;
  return <XCircle size={14} className="text-[#EF4444]" />;
};

const readinessCards = [
  { title: 'Internal Audit Readiness', score: 84, color: '#10B981', lines: ['Population available for last 4 quarters', 'Exception register current'], link: { text: 'Auditor Workspace ready', to: '/dashboard/evidence-vault' } },
  { title: 'SOC 2 AI Evidence Coverage', score: 61, color: '#F59E0B', lines: ['8 controls with evidence gaps', 'Next examination: Q3 2026'], link: { text: 'View SOC 2 gaps', to: '/dashboard/compliance' } },
  { title: 'Insurance Evidence Readiness', score: 81, color: '#10B981', lines: ['Insurance Underwriting Package current', 'Last renewal: Feb 15, 2026'], link: { text: 'Generate renewal package', to: '/dashboard/assurance' } },
];

const sectorProfiles = [
  { title: 'Financial Services (PCI DSS / FFIEC)', desc: 'AI components in payment, fraud, and lending workflows. Maps Decision Ledger, Change Control, and Agent Controls to financial services audit requirements.' },
  { title: 'Healthcare (HIPAA / HITRUST)', desc: 'AI systems handling PHI or clinical decisions. Maps Data Access Records, human oversight documentation, and exception trails to healthcare compliance contexts.' },
  { title: 'US Federal / FedRAMP', desc: 'AI components in cloud services for government. Maps NIST 800-53 AU, AC, CM, and IR families to federal procurement and FedRAMP authorization contexts.' },
  { title: 'EU Network & Information Security (NIS2)', desc: 'AI-related incident reporting, logging, and risk management for essential and important entities. Maps Incident Review, Evidence Vault, and Change Control to NIS2 obligations.' },
];

function MiniRing({ score, color, size = 64 }) {
  const data = [{ value: score }, { value: 100 - score }];
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={size * 0.38} outerRadius={size * 0.48} startAngle={90} endAngle={-270} dataKey="value" stroke="none">
            <Cell fill={color} />
            <Cell fill="#1E2A45" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-[#F8FAFC]">{score}</div>
    </div>
  );
}

function ExportModal() {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-4 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
        <Shield size={13} /> ◆ Generate Proof Chain Export
      </button>
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-[#0F1629] border border-[#1E2A45] rounded-xl p-6 max-w-lg w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="export-scope-modal">
            <h3 className="text-sm font-semibold text-[#F8FAFC] uppercase tracking-wider">Configure Proof Chain Export</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[10px] text-[#475569] mb-1">From</div><input type="text" defaultValue="Apr 1, 2026" className="w-full text-xs bg-[#080D1A] border border-[#1E2A45] rounded-lg px-3 py-2 text-[#F8FAFC]" /></div>
                <div><div className="text-[10px] text-[#475569] mb-1">To</div><input type="text" defaultValue="Apr 15, 2026" className="w-full text-xs bg-[#080D1A] border border-[#1E2A45] rounded-lg px-3 py-2 text-[#F8FAFC]" /></div>
              </div>
              <div>
                <div className="text-[10px] text-[#475569] mb-1.5">Workflows</div>
                {['Legal Document Drafting', 'Contract Classification', 'Claims Processing', 'Academy Recommendations', 'Webcast Metadata'].map(w => (
                  <label key={w} className="flex items-center gap-2 text-[10px] text-[#94A3B8] mb-1"><input type="checkbox" defaultChecked className="accent-[#6366F1]" />{w}</label>
                ))}
              </div>
              <div>
                <div className="text-[10px] text-[#475569] mb-1.5">Minimum proof grade</div>
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-[10px] text-[#94A3B8]"><input type="radio" name="grade" defaultChecked className="accent-[#10B981]" />Audit-Grade only</label>
                  <label className="flex items-center gap-2 text-[10px] text-[#94A3B8]"><input type="radio" name="grade" className="accent-[#F59E0B]" />Operations-Grade and above</label>
                  <label className="flex items-center gap-2 text-[10px] text-[#94A3B8]"><input type="radio" name="grade" className="accent-[#475569]" />All</label>
                </div>
              </div>
              <div>
                <div className="text-[10px] text-[#475569] mb-1.5">Include</div>
                {['Classification Evidence Records', 'Control State Snapshots', 'Decision Evidence Records', 'Oversight Evidence Records', 'Chain Integrity Timeline', 'Integration corroboration manifest'].map(i => (
                  <label key={i} className="flex items-center gap-2 text-[10px] text-[#94A3B8] mb-1"><input type="checkbox" defaultChecked className="accent-[#6366F1]" />{i}</label>
                ))}
              </div>
              <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45] text-[10px] text-[#64748B]">
                Estimated records: <span className="text-[#F8FAFC] font-mono">38,420 DERs · 5 CERs · 38,420 CSSs · 38,209 OERs</span><br/>
                Estimated package size: <span className="text-[#F8FAFC] font-mono">47 MB</span>
              </div>

              {/* Evidence Quality Notice — institutional honesty */}
              <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]" data-testid="evidence-quality-notice">
                <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-[0.15em] mb-2">Evidence Quality Notice</div>
                <div className="text-[10px] text-[#64748B] mb-2">This export includes records across multiple capture methods:</div>
                <div className="space-y-1 font-mono text-[10px]">
                  <div className="flex items-baseline gap-2"><span className="text-[#10B981] w-32 flex-shrink-0">◆ AUDIT-GRADE</span><span className="text-[#F8FAFC] w-16 text-right">38,209</span><span className="text-[#64748B]">SDK capture · all required fields present</span></div>
                  <div className="flex items-baseline gap-2"><span className="text-[#F59E0B] w-32 flex-shrink-0">◆ OPS-GRADE</span><span className="text-[#F8FAFC] w-16 text-right">189</span><span className="text-[#64748B]">Log reconstruction · some fields estimated</span></div>
                  <div className="flex items-baseline gap-2"><span className="text-[#94A3B8] w-32 flex-shrink-0">◆ OBSERVATIONAL</span><span className="text-[#F8FAFC] w-16 text-right">22</span><span className="text-[#64748B]">Partial capture · labeled with limitations</span></div>
                </div>
                <div className="text-[10px] text-[#64748B] mt-2 leading-relaxed">
                  Audit-Grade records are suitable for external submission. Operations-Grade and Observational records are labeled explicitly and may require supplemental attestation for external audit use.
                </div>
                <div className="text-[10px] text-[#475569] mt-1.5">Apex provides evidence inputs. Your licensed audit firm provides the attestation opinion.</div>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button onClick={() => { setShowModal(false); toast.success('Proof Chain Export generating — 38,420 records across 5 workflows', { description: 'Download ready in ~30 seconds.' }); }} className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-4 py-2.5 rounded-lg transition-all">Generate Export →</button>
              <button onClick={() => setShowModal(false)} className="text-xs text-[#475569] hover:text-[#94A3B8]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AssuranceCenter() {
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const [pccDetailsOpen, setPccDetailsOpen] = useState(false);

  return (
    <div data-testid="assurance-center" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-[#94A3B8]">Generate regulator-ready evidence packages for auditors, insurers, and board committees.</div>
        <Link to="/dashboard/regulatory-moment" data-testid="regulatory-moment-link" className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1 font-medium">
          Regulatory Moment of Truth <ArrowRight size={12} />
        </Link>
      </div>

      {/* Proof Chain Completeness Card — calm 4-metric summary, details collapsed */}
      <div className="glass-card p-5" data-testid="proof-chain-completeness">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} className="text-[#6366F1]" />
          <h3 className="text-sm font-semibold text-[#F8FAFC]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Proof Chain Completeness</h3>
          <span className="text-[9px] text-[#64748B]">Across 5 governed workflows · Apr 15, 2026 · 14:32 UTC</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-[11px]"><span className="text-[#10B981]">◆</span><span className="text-[#94A3B8] w-40">Classifications:</span><span className="text-[#F8FAFC]">5/5 current</span></div>
          <div className="flex items-center gap-2 text-[11px]"><span className="text-[#10B981]">◆</span><span className="text-[#94A3B8] w-40">Decisions:</span><span className="text-[#F8FAFC]">47,832 signed</span></div>
          <div className="flex items-center gap-2 text-[11px]"><span className="text-[#F59E0B]">⚠</span><span className="text-[#94A3B8] w-40">Control State:</span><span className="text-[#F8FAFC]">94% — 3 gaps under remediation</span></div>
          <div className="flex items-center gap-2 text-[11px]"><span className="text-[#F59E0B]">⚠</span><span className="text-[#94A3B8] w-40">Oversight:</span><span className="text-[#F8FAFC]">97% — 3 reviews below threshold</span></div>
        </div>
        <button
          data-testid="pcc-details-toggle"
          onClick={() => setPccDetailsOpen(!pccDetailsOpen)}
          className="mt-3 text-[10px] text-[#6366F1] hover:text-[#818cf8] flex items-center gap-1"
        >
          {pccDetailsOpen ? 'Hide details' : 'View details'} <ArrowRight size={10} className={pccDetailsOpen ? 'rotate-90' : ''} />
        </button>
        {pccDetailsOpen && (
          <div className="mt-3 pt-3 border-t border-[#1E2A45] space-y-2" data-testid="pcc-details">
            <div className="text-[10px] text-[#F8FAFC] font-semibold">Overall Proof Chain Grade: <GradeTooltip grade="OPERATIONS-GRADE"><span className="text-[#F59E0B] cursor-help">◆ OPERATIONS-GRADE</span></GradeTooltip></div>
            <div className="text-[9px] text-[#94A3B8]">2 items needed for full AUDIT-GRADE:</div>
            <div className="text-[9px] text-[#94A3B8]">→ Close 3 CSS gaps (Owner: IT Security · J. Reyes · Due: Apr 22, 2026)</div>
            <div className="text-[9px] text-[#94A3B8]">→ Resolve 3 OER escalations (Owner: Head of Compliance · J. Mueller · Due: Apr 20, 2026)</div>
            <Link to="/dashboard/proof-chain" className="text-[10px] text-[#6366F1] hover:text-[#818cf8] flex items-center gap-1 mt-1">View Proof Chain → <ArrowRight size={10} /></Link>
            <div className="text-[9px] text-[#475569]">Chain Integrity Engine · Last evaluated: 4 minutes ago · Next evaluation: automatic</div>
          </div>
        )}
      </div>

      {/* Pilot Offer */}
      <div className="text-[10px] text-[#475569] px-1" data-testid="pilot-offer">
        <span className="text-[#64748B]">Design partner pilot:</span> First end-to-end proof chain in 7 days. SDK wrapper on one workflow → complete CER, CSS, DER, OER, and Evidence Package.
      </div>

      {/* Assurance & Audit Readiness */}
      <div data-testid="assurance-readiness-section">
        <h3 className="text-sm font-semibold text-[#F8FAFC] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Assurance & Audit Readiness</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {readinessCards.map(card => (
            <div key={card.title} className="glass-card p-5" data-testid={`readiness-${card.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-xs font-semibold text-[#F8FAFC] mb-2">{card.title}</h4>
                  <div className="space-y-1 mb-2">
                    {card.lines.map(l => <div key={l} className="text-[10px] text-[#64748B]">{l}</div>)}
                  </div>
                  <Link to={card.link.to} className="text-[10px] text-[#6366F1] hover:text-[#818cf8] flex items-center gap-1">{card.link.text} <ArrowRight size={10} /></Link>
                </div>
                <MiniRing score={card.score} color={card.color} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Package Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {packages.map((pkg) => {
          const barColor = pkg.completeness >= 80 ? '#10B981' : pkg.completeness > 0 ? '#F59E0B' : '#EF4444';
          return (
            <div key={pkg.id} className="glass-card p-6" data-testid={`package-${pkg.id}`}>
              <div className="flex items-start gap-3 mb-4">
                <FileText size={20} className="text-[#6366F1] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-[#F8FAFC] mb-1">{pkg.title}</h3>
                  <p className="text-xs text-[#64748B] leading-relaxed">{pkg.desc}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-[#475569] uppercase">Evidence completeness</span>
                    <span className="text-xs font-mono" style={{ color: barColor }}>{pkg.completeness}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#1E2A45] overflow-hidden">
                    <div className="h-full rounded-full progress-bar-fill" style={{ width: `${pkg.completeness}%`, backgroundColor: barColor }} />
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#64748B]">Records: <span className="font-mono text-[#94A3B8]">{pkg.records.toLocaleString()}</span></span>
                  <span className="text-[#475569]">Last: {pkg.lastGen}</span>
                </div>
                <button data-testid={`generate-${pkg.id}`} onClick={() => { exportAssurancePackagePDF(pkg); toast.success(`${pkg.title} downloaded as PDF`); }} className="w-full flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-4 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
                  <Download size={13} /> Generate Package
                </button>
                {pkg.disclaimer && (
                  <p className="text-[10px] text-[#64748B] leading-relaxed text-center mt-1">{pkg.disclaimer}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Framework Crosswalk Matrix */}
      <div className="glass-card p-6" data-testid="crosswalk-matrix">
        <h3 className="text-sm font-semibold text-[#F8FAFC] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Framework Crosswalk Matrix</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1E2A45]">
                <th className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-4">Control Category</th>
                <th className="text-center text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-4">EU AI Act</th>
                <th className="text-center text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-4">ISO 42001</th>
                <th className="text-center text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-4">NIST AI RMF</th>
                <th className="text-center text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-4">SOC 2</th>
              </tr>
            </thead>
            <tbody>
              {crosswalkRows.map((row) => (
                <tr key={row.category} className="border-b border-[#1E2A45]/50">
                  <td className="py-3 px-4 text-[#F8FAFC]">{row.category}</td>
                  <td className="py-3 px-4 text-center">{statusIcon(row.eu)}</td>
                  <td className="py-3 px-4 text-center">{statusIcon(row.iso)}</td>
                  <td className="py-3 px-4 text-center">{statusIcon(row.nist)}</td>
                  <td className="py-3 px-4 text-center">{statusIcon(row.soc2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-6 mt-4 pt-3 border-t border-[#1E2A45]">
          <div className="flex items-center gap-1.5 text-[10px] text-[#10B981]"><CheckCircle2 size={11} /> Covered</div>
          <div className="flex items-center gap-1.5 text-[10px] text-[#F59E0B]"><AlertTriangle size={11} /> Partial</div>
          <div className="flex items-center gap-1.5 text-[10px] text-[#EF4444]"><XCircle size={11} /> Gap</div>
        </div>
      </div>

      {/* Complete Proof Chain Export Card */}
      <div className="glass-card p-5 border-t-2 border-t-[#6366F1]" data-testid="proof-chain-export">
        <h3 className="text-sm font-semibold text-[#F8FAFC] mb-2">Complete Proof Chain Export</h3>
        <div className="text-[10px] text-[#94A3B8] mb-3 leading-relaxed">
          Export the full governance proof chain for any workflow or time period. Includes: Classification Evidence Records · Control State Snapshots · Decision Evidence Records · Oversight Evidence Records · Evidence Package. One signed ZIP. One proof-grade story.
        </div>
        <ExportModal />
      </div>

      {/* Sector-Specific Evidence Profiles */}
      <div className="glass-card overflow-hidden" data-testid="sector-profiles">
        <button onClick={() => setSectorsOpen(!sectorsOpen)} className="w-full flex items-center justify-between p-5 text-left">
          <h3 className="text-sm font-semibold text-[#F8FAFC]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Sector-Specific Evidence Profiles</h3>
          <ChevronDown size={16} className={`text-[#475569] transition-transform ${sectorsOpen ? 'rotate-180' : ''}`} />
        </button>
        {sectorsOpen && (
          <div className="px-5 pb-5">
            <div className="text-[10px] text-[#64748B] mb-4">Profile exports use existing evidence — no new data collection required</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sectorProfiles.map(sp => (
                <div key={sp.title} className="p-4 rounded-lg bg-[#080D1A] border border-[#1E2A45]/60">
                  <h4 className="text-xs font-semibold text-[#F8FAFC] mb-2">{sp.title}</h4>
                  <p className="text-[10px] text-[#64748B] leading-relaxed mb-3">{sp.desc}</p>
                  <button onClick={() => { exportSectorProfilePDF(sp.title); toast.success(`${sp.title} profile downloaded`); }} className="text-[10px] text-[#94A3B8] hover:text-[#6366F1] border border-[#1E2A45] hover:border-[#6366F1]/30 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1">
                    Generate profile <ArrowRight size={10} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
